import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { decisaoDeConsentimento, registrarConsentimento } from "services/analytics";
import { ehNativo } from "services/plataforma";

/**
 * Faixa de consentimento de cookies, fixa acima de todas as páginas.
 *
 * Era o único componente escrito inteiramente em `style` inline: cores cravadas, `fontSize: "14px"`
 * onde o `.t-apoio` já dizia a mesma coisa, e um `borderRadius: "18px"` que não existia em nenhum
 * outro lugar do site — numa superfície que flutua sobre todas as telas, justamente onde um raio
 * fora do sistema é mais visível.
 *
 * <h2>Não aparece no app</h2>
 * Este banner fala de cookies de navegação e do Google Analytics do SITE. Dentro do app não há
 * navegador, não há terceiro gravando cookie, e a faixa apareceria falando de uma coisa que não está
 * acontecendo — pior, cobrindo o rodapé da primeira tela para pedir permissão sobre nada.
 *
 * No app, quem pergunta é `ConsentimentoApp` — com texto de aplicativo, e não esta faixa herdada da
 * web. As duas escrevem a MESMA decisão (ver services/analytics.js), então existe uma só resposta
 * por aparelho, não uma por superfície.
 *
 * <h2>Um botão só, e a medição continua dependendo dele</h2>
 * A faixa já teve "Recusar" ao lado de "Aceitar"; saiu a pedido, para simplificar a primeira tela.
 * O que NÃO mudou é o sentido do clique: nada é medido enquanto a pessoa não aceitar. Ignorar a
 * faixa equivale a não autorizar — por isso o texto diz "ao clicar", e não "ao continuar
 * navegando", que seria prometer um consentimento tácito que o código não pratica.
 *
 * Quem recusou antes (ou recusa nas configurações da conta, pages/Usuario/Editar/MinhaConta.jsx)
 * continua sem ver a faixa: `decisaoDeConsentimento()` distingue recusa de silêncio.
 *
 * No app a recusa continua existindo, em `ConsentimentoApp` — a Play Store pede que recusar não
 * custe acesso, e esta faixa não aparece lá.
 */
const CookieBanner = () => {
  const [showBanner, setShowBanner] = useState(false);

  useEffect(() => {
    if (ehNativo()) return;

    // `decisaoDeConsentimento()`, e não "tem consentimento": quem recusou também já decidiu, e
    // voltar a perguntar seria ignorar a resposta que a pessoa acabou de dar.
    if (decisaoDeConsentimento() === null) {
      setShowBanner(true);
    }
  }, []);

  // O evento disparado lá dentro é o que liga o Google Analytics nesta mesma visita: sem ele o
  // aceite só valeria na próxima vez que a pessoa abrisse o site, porque é no carregamento que o
  // consentimento guardado é lido. Ver services/analytics.js.
  const aceitar = () => {
    registrarConsentimento(true);
    setShowBanner(false);
  };

  if (!showBanner) {
    return null;
  }

  return (
    <div className="fixed inset-x-4 bottom-4 z-[1000] flex justify-center">
      {/* bg-dark/95: o navy da marca, no lugar do cinza-azulado avulso de antes. */}
      <div className="flex w-full max-w-[960px] flex-wrap items-center justify-between gap-4 rounded-2xl bg-dark/95 px-5 py-[18px] text-white shadow-2xl">
        <div className="flex-[1_1_420px]">
          <p className="t-apoio text-white/90">
            Usamos cookies e tecnologias semelhantes para manter seu login,
            lembrar suas preferências e entender como o site é usado, para
            melhorá-lo. Ao clicar em &ldquo;Aceitar e continuar&rdquo;, você
            concorda com essa utilização. Saiba mais na nossa{" "}
            <Link
              to="/politica-de-cookies"
              className="font-semibold text-accent underline underline-offset-[3px]"
            >
              Política de Cookies
            </Link>{" "}
            e na{" "}
            <Link
              to="/politica-de-privacidade"
              className="font-semibold text-accent underline underline-offset-[3px]"
            >
              Política de Privacidade
            </Link>
            .
          </p>
        </div>

        <div className="flex flex-[0_0_auto]">
          <button
            type="button"
            onClick={aceitar}
            className="btn bg-success text-white hover:bg-success/90 focus:ring-success focus:ring-offset-dark"
          >
            Aceitar e continuar
          </button>
        </div>
      </div>
    </div>
  );
};

export default CookieBanner;
